import { useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { asignacionCubre, bordesDeVigencia } from '@practiclinic/core'
import {
  agendaDelDia,
  rangoDelDia,
  recursosVisibles,
  type TramoConCita,
} from '../datos/consultas.js'
import { db } from '../datos/db.js'
import { fechaLarga, hora, hoy, limitesDelDia, minutosDelDia, sumarDias } from '../datos/formato.js'
import { moverCitaLocal } from '../datos/mover.js'

/**
 * Timeline de recepción (T06).
 *
 * **Por qué esto no es una grilla de celdas.** La primera versión lo era, y
 * estaba mal: con CSS Grid y auto-placement, una celda que no se emite —porque
 * la tapa una cita larga de arriba— hace que todas las columnas siguientes se
 * corran un lugar en esa fila. El resultado no era feo: era **incorrecto**. Las
 * citas se dibujaban bajo el profesional equivocado.
 *
 * Acá cada columna es un contenedor propio y cada cita se ubica con `top` y
 * `height` calculados desde su hora. Nada depende de que el navegador decida
 * dónde poner las cosas.
 *
 * Lo que **no** hay que resolver: solapamiento visual dentro de una columna. El
 * constraint de exclusión garantiza que dos citas no pueden ocupar el mismo
 * recurso a la vez, así que el caso clásico de "eventos superpuestos lado a
 * lado" no existe por construcción (§8.6).
 */

/** Píxeles por minuto. A 1.6, media hora son 48 px: entran dos líneas cómodas. */
const PX_POR_MIN = 1.6

/**
 * Colores por cita.
 *
 * Con fondo `-50` los bloques quedaban casi blancos y la pantalla entera se
 * veía desvaída. Con `-100` y borde `-200` tienen cuerpo sin gritar, que es lo
 * que hace falta cuando hay treinta en pantalla.
 */
const PALETA = [
  { fondo: 'bg-teal-100', borde: 'border-teal-300', barra: 'bg-teal-600', texto: 'text-teal-950' },
  { fondo: 'bg-sky-100', borde: 'border-sky-300', barra: 'bg-sky-600', texto: 'text-sky-950' },
  { fondo: 'bg-violet-100', borde: 'border-violet-300', barra: 'bg-violet-600', texto: 'text-violet-950' },
  { fondo: 'bg-amber-100', borde: 'border-amber-300', barra: 'bg-amber-600', texto: 'text-amber-950' },
  { fondo: 'bg-rose-100', borde: 'border-rose-300', barra: 'bg-rose-600', texto: 'text-rose-950' },
  { fondo: 'bg-emerald-100', borde: 'border-emerald-300', barra: 'bg-emerald-600', texto: 'text-emerald-950' },
]

function colorDe(citaId: string) {
  let acumulado = 0
  for (let i = 0; i < citaId.length; i++) acumulado = (acumulado + citaId.charCodeAt(i)) % 997
  return PALETA[acumulado % PALETA.length]!
}

/**
 * La cita que se está arrastrando.
 *
 * `minutoTomado` es el punto exacto del bloque donde se agarró: sin él, soltar
 * mueve la cita al minuto del cursor y no al que le corresponde, así que
 * agarrar una cita de una hora por el medio la correría media hora.
 */
interface Arrastre {
  citaId: string
  recursoOrigen: string
  minutoInicio: number
  minutoTomado: number
}

export interface TimelineProps {
  dia: string
  onDia: (d: string) => void
  slotMin: number
  citaEnfocada?: string | null
  agendarPara?: string | null
  onCancelarAgendar?: () => void
  onNuevaCita?: ((recursoId: string, inicioIso: string) => void) | undefined
  onAbrirCita?: ((citaId: string) => void) | undefined
}

export function Timeline({ dia, onDia, slotMin, citaEnfocada, agendarPara, onCancelarAgendar, onNuevaCita, onAbrirCita }: TimelineProps) {
  const [agrupacion, setAgrupacion] = useState<'persona' | 'espacio'>('persona')
  const [citaResaltada, setCitaResaltada] = useState<string | null>(null)
  const [arrastrando, setArrastrando] = useState<Arrastre | null>(null)
  const scroll = useRef<HTMLDivElement>(null)

  const recursos = useLiveQuery(() => recursosVisibles(), [], [])
  const tramos = useLiveQuery(() => agendaDelDia(dia), [dia], [])

  const columnas = useMemo(
    () => recursos.filter((r) => (agrupacion === 'persona' ? r.esPersona : !r.esPersona)),
    [recursos, agrupacion],
  )

  /**
   * Quién tiene consultorio asignado hoy.
   *
   * Sin una franja publicada que lo cubra, el motor no ofrece ni un turno para
   * esa persona (§3.8). La agenda tiene que mostrarlo: si no, la columna se ve
   * igual que una sin citas y no hay forma de saber por qué no se puede agendar.
   */
  const conFranja = useLiveQuery(async () => {
    const { inicio, fin } = limitesDelDia(dia)
    const ventana = { inicio: new Date(inicio), fin: new Date(fin) }
    const franjas = await db.asignacion_espacio
      .filter(
        (a) =>
          a.estado === 'publicada' &&
          a.deleted_at === null &&
          asignacionCubre(
            {
              id: a.id,
              personaId: a.persona_id,
              espacioId: a.espacio_id,
              diaSemana: a.dia_semana,
              desde: a.desde,
              hasta: a.hasta,
              ...bordesDeVigencia(a.vigencia),
            },
            ventana,
          ),
      )
      .toArray()
    return new Set([
      ...franjas.map((a) => a.persona_id),
      ...franjas.map((a) => a.espacio_id),
    ])
  }, [dia])

  const rango = useLiveQuery(
    () => rangoDelDia(dia, columnas.map((c) => c.id)),
    [dia, columnas.map((c) => c.id).join()],
    { desde: 8, hasta: 20 },
  )

  const altoTotal = (rango.hasta - rango.desde) * 60 * PX_POR_MIN

  const porRecurso = useMemo(() => {
    const mapa = new Map<string, TramoConCita[]>()
    for (const t of tramos) {
      for (const recursoId of t.recursos) {
        const lista = mapa.get(recursoId) ?? []
        lista.push(t)
        mapa.set(recursoId, lista)
      }
    }
    return mapa
  }, [tramos])

  const horas = useMemo(
    () => Array.from({ length: rango.hasta - rango.desde }, (_, i) => rango.desde + i),
    [rango.desde, rango.hasta],
  )

  const esHoy = dia === hoy()

  useEffect(() => {
    setCitaResaltada(citaEnfocada ?? null)
  }, [citaEnfocada])

  const agendarPaciente = useLiveQuery(
    () => (agendarPara ? db.cliente.get(agendarPara) : undefined),
    [agendarPara],
  )

  // La línea de "ahora" se mueve sola. Cada minuto alcanza: es una agenda, no
  // un cronómetro.
  const [ahora, setAhora] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])

  const minutosAhora = ahora.getHours() * 60 + ahora.getMinutes()
  const ahoraVisible =
    esHoy && minutosAhora >= rango.desde * 60 && minutosAhora <= rango.hasta * 60
  const topAhora = (minutosAhora - rango.desde * 60) * PX_POR_MIN

  // Al abrir el día se baja hasta la hora actual. Sin esto uno aterriza a las
  // 08:00 y tiene que buscar dónde está parado.
  //
  // La marca por día es lo que evita que la vista se mueva sola: el minutero
  // actualiza `topAhora` cada 60 s, y sin esto la pantalla saltaría mientras
  // alguien está leyendo.
  const yaCentrado = useRef<string | null>(null)
  useEffect(() => {
    if (!scroll.current || !ahoraVisible) return
    if (yaCentrado.current === dia) return
    yaCentrado.current = dia
    scroll.current.scrollTop = Math.max(0, topAhora - 160)
  }, [dia, ahoraVisible, topAhora])

  return (
    <div className="flex h-full flex-col superficie-base">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onDia(sumarDias(dia, -1))}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Día anterior"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => onDia(sumarDias(dia, 1))}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Día siguiente"
          >
            ›
          </button>
        </div>

        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-semibold leading-tight text-slate-900 first-letter:uppercase">
            {fechaLarga(`${dia}T12:00:00Z`)}
          </h2>
          <p className="text-xs text-slate-500">
            {tramos.length === 0 ? 'sin citas agendadas' : `${contarCitas(tramos)} citas`}
          </p>
        </div>

        {!esHoy && (
          <button
            type="button"
            onClick={() => onDia(hoy())}
            className="h-8 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Volver a hoy
          </button>
        )}

        <div className="ml-auto flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          {(['persona', 'espacio'] as const).map((modo) => (
            <button
              key={modo}
              type="button"
              onClick={() => setAgrupacion(modo)}
              className={`h-8 rounded-md px-3 font-medium transition ${
                agrupacion === modo
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {modo === 'persona' ? 'Profesionales' : 'Consultorios'}
            </button>
          ))}
        </div>
        {agendarPara && agendarPaciente && (
          <div className="flex w-full items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800">
            <span className="flex-1">
              Elegí el horario para <strong>{agendarPaciente.nombre}</strong>
            </span>
            {onCancelarAgendar && (
              <button
                type="button"
                onClick={onCancelarAgendar}
                className="text-xs text-teal-600 underline hover:text-teal-900"
              >
                Cancelar
              </button>
            )}
          </div>
        )}
      </header>

      {columnas.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-10 text-center">
          <div>
            <p className="text-slate-600">
              No hay {agrupacion === 'persona' ? 'profesionales' : 'consultorios'} cargados.
            </p>
            <p className="mt-1 text-sm text-slate-400">
              Un administrador los agrega desde Configuración.
            </p>
          </div>
        </div>
      ) : (
        <div ref={scroll} className="flex-1 overflow-auto">
          <div className="flex min-w-full">
            {/* Regla de horas, fija a la izquierda: al desplazarse por una
                agenda ancha no se pierde la referencia. */}
            <div className="sticky left-0 z-30 w-16 shrink-0 superficie-base">
              <div className="sticky top-0 z-40 h-11 border-b border-r border-slate-200 bg-white" />
              <div className="relative border-r border-slate-200" style={{ height: altoTotal }}>
                {horas.map((h, i) => (
                  <span
                    key={h}
                    className={`absolute right-2 text-[11px] font-medium tabular-nums text-slate-400 ${
                      i === 0 ? '' : '-translate-y-1/2'
                    }`}
                    style={{ top: (h - rango.desde) * 60 * PX_POR_MIN }}
                  >
                    {String(h).padStart(2, '0')}:00
                  </span>
                ))}

                {ahoraVisible && (
                  <span
                    className="absolute right-1 -translate-y-1/2 rounded bg-rose-600 px-1 py-px text-[10px] font-semibold tabular-nums text-white"
                    style={{ top: topAhora }}
                  >
                    {String(ahora.getHours()).padStart(2, '0')}:
                    {String(ahora.getMinutes()).padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>

            {columnas.map((recurso) => (
              <ColumnaRecurso
                key={recurso.id}
                nombre={recurso.nombre}
                recursoId={recurso.id}
                citas={porRecurso.get(recurso.id) ?? []}
                horas={horas}
                desde={rango.desde}
                alto={altoTotal}
                slotMin={slotMin}
                dia={dia}
                topAhora={ahoraVisible ? topAhora : null}
                citaResaltada={citaResaltada}
                onResaltar={setCitaResaltada}
                sinFranja={conFranja !== undefined && !conFranja.has(recurso.id)}
                arrastrando={arrastrando}
                onArrastrar={setArrastrando}
                onSoltar={async (recursoDestino, minutoSoltado) => {
                  const a = arrastrando
                  setArrastrando(null)
                  if (!a) return
                  // El delta sale del punto donde se agarró el bloque, no de su
                  // inicio: si no, agarrar una cita larga por el medio la corre
                  // de más.
                  const deltaMin =
                    Math.round((minutoSoltado - (a.minutoTomado - a.minutoInicio)) / slotMin) *
                      slotMin -
                    a.minutoInicio
                  if (deltaMin === 0 && recursoDestino === a.recursoOrigen) return
                  await moverCitaLocal({
                    citaId: a.citaId,
                    recursoIdDestino: recursoDestino,
                    deltaMin,
                  })
                }}
                onAbrirCita={onAbrirCita}
                onNuevaCita={onNuevaCita ? (iso) => onNuevaCita(recurso.id, iso) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function contarCitas(tramos: TramoConCita[]): number {
  return new Set(tramos.map((t) => t.cita.id)).size
}

interface ColumnaProps {
  nombre: string
  recursoId: string
  citas: TramoConCita[]
  horas: number[]
  desde: number
  alto: number
  slotMin: number
  dia: string
  topAhora: number | null
  citaResaltada: string | null
  onResaltar: (id: string | null) => void
  arrastrando: Arrastre | null
  onArrastrar: (a: Arrastre | null) => void
  onSoltar: (recursoDestino: string, minutoSoltado: number) => void
  onAbrirCita?: ((citaId: string) => void) | undefined
  onNuevaCita?: ((inicioIso: string) => void) | undefined
  /** Sin franja publicada no se ofrecen turnos acá, y hay que decirlo (§3.8). */
  sinFranja?: boolean
}

function ColumnaRecurso({
  nombre,
  recursoId,
  citas,
  horas,
  desde,
  alto,
  slotMin,
  dia,
  topAhora,
  citaResaltada,
  onResaltar,
  arrastrando,
  onArrastrar,
  onSoltar,
  onAbrirCita,
  onNuevaCita,
  sinFranja = false,
}: ColumnaProps) {
  const cuerpo = useRef<HTMLDivElement>(null)

  /** Minuto del día donde ocurrió el evento, sin redondear. */
  function minutoDelEvento(ev: { clientY: number }): number {
    if (!cuerpo.current) return desde * 60
    const y = ev.clientY - cuerpo.current.getBoundingClientRect().top
    return desde * 60 + y / PX_POR_MIN
  }

  /** Dónde se hizo clic, redondeado hacia abajo a la grilla de `slotMin`. */
  function slotDelClic(ev: React.MouseEvent<HTMLDivElement>): string | null {
    if (!cuerpo.current) return null
    const y = ev.clientY - cuerpo.current.getBoundingClientRect().top
    const minutos = desde * 60 + Math.floor(y / PX_POR_MIN / slotMin) * slotMin
    const hh = String(Math.floor(minutos / 60)).padStart(2, '0')
    const mm = String(minutos % 60).padStart(2, '0')
    return `${dia}T${hh}:${mm}`
  }

  const ocupadas = citas.length

  return (
    <div className="min-w-40 flex-1 border-r border-slate-200 last:border-r-0">
      <div className="sticky top-0 z-20 flex h-11 items-center gap-2 border-b border-slate-200 bg-white px-3">
        <span className="truncate text-sm font-semibold text-slate-800" title={nombre}>
          {nombre}
        </span>
        {sinFranja ? (
          // Una columna vacía porque no hay consultorio asignado no es lo
          // mismo que una columna vacía porque no hay citas, y hasta acá era
          // indistinguible: la recepcionista intentaba agendar y no entendía
          // por qué no había horarios.
          <span
            className="ml-auto shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
            title="Sin una franja publicada en Consultorios → Plantilla semanal, el motor no ofrece turnos para esta persona"
          >
            sin consultorio
          </span>
        ) : (
          ocupadas > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-slate-100 px-1.5 text-[11px] font-medium tabular-nums text-slate-500">
              {ocupadas}
            </span>
          )
        )}
      </div>

      <div
        ref={cuerpo}
        className={`relative transition-colors ${
          // Mientras se arrastra, la columna candidata se ilumina: sin eso uno
          // no sabe dónde va a caer la cita hasta que suelta.
          arrastrando
            ? 'bg-teal-50/60'
            : sinFranja
              ? 'bg-slate-50'
              : 'bg-white hover:bg-slate-50/40'
        }`}
        style={{ height: alto }}
        onClick={(ev) => {
          // Sólo si el clic fue en el fondo: sobre una cita manda la cita.
          if (ev.target !== ev.currentTarget) return
          const iso = slotDelClic(ev)
          if (iso) onNuevaCita?.(iso)
        }}
        onDragOver={(ev) => {
          // Sin esto el navegador no considera la columna un destino válido y
          // el cursor muestra el ícono de "prohibido".
          if (arrastrando) ev.preventDefault()
        }}
        onDrop={(ev) => {
          ev.preventDefault()
          onSoltar(recursoId, minutoDelEvento(ev))
        }}
      >
        {/* Franjas por hora. Decorativas: `pointer-events-none` para no comerse
            el clic de agendar. */}
        {horas.map((h, i) => (
          <div
            key={h}
            className={`pointer-events-none absolute inset-x-0 border-t ${
              i === 0 ? 'border-transparent' : 'border-slate-100'
            }`}
            style={{
              top: (h - desde) * 60 * PX_POR_MIN,
              height: 60 * PX_POR_MIN,
              // Zebra muy tenue: guía el ojo por la fila sin ensuciar.
              background: i % 2 === 1 ? 'rgba(148,163,184,0.045)' : undefined,
            }}
          />
        ))}

        {topAhora !== null && (
          <div
            className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-rose-500"
            style={{ top: topAhora }}
          />
        )}

        {citas.map(({ tramo, cita, cliente }) => {
          const inicio = minutosDelDia(tramo.inicio)
          const fin = minutosDelDia(tramo.fin)
          const top = (inicio - desde * 60) * PX_POR_MIN
          const altoBloque = Math.max(22, (fin - inicio) * PX_POR_MIN)
          const color = colorDe(cita.id)
          const resaltada = citaResaltada === cita.id
          // Por debajo de 38 px no entran dos líneas sin cortarse.
          const compacta = altoBloque < 38
          const cerrada = cita.estado === 'atendida' || cita.estado === 'facturada'

          return (
            <button
              key={tramo.id}
              type="button"
              draggable
              onDragStart={(ev) => {
                ev.dataTransfer.effectAllowed = 'move'
                // Firefox no inicia el arrastre si no hay datos puestos.
                ev.dataTransfer.setData('text/plain', cita.id)
                onArrastrar({
                  citaId: cita.id,
                  recursoOrigen: recursoId,
                  minutoInicio: inicio,
                  minutoTomado: minutoDelEvento(ev),
                })
              }}
              onDragEnd={() => onArrastrar(null)}
              onClick={() => onAbrirCita?.(cita.id)}
              onMouseEnter={() => onResaltar(cita.id)}
              onMouseLeave={() => onResaltar(null)}
              // Una cita que ocupa dos recursos se dibuja en dos columnas: el
              // color y el resaltado son el vínculo visual entre sus bloques.
              className={`group absolute inset-x-1.5 flex overflow-hidden rounded-md border text-left transition ${color.fondo} ${color.borde} ${color.texto} ${
                resaltada ? 'z-20 shadow-lg ring-2 ring-slate-900/25' : 'shadow-sm'
              } ${cerrada ? 'opacity-75 saturate-50' : 'cursor-grab active:cursor-grabbing'} ${
                arrastrando?.citaId === cita.id ? 'opacity-40' : ''
              }`}
              style={{ top, height: altoBloque }}
              title={`${cliente?.nombre ?? 'Sin cliente'} · ${hora(tramo.inicio)}–${hora(tramo.fin)} · ${etiquetaEstado(cita.estado)}`}
            >
              {/* La barra de color es lo que se reconoce de reojo cuando la
                  misma cita aparece en dos columnas. */}
              <span className={`w-1 shrink-0 ${color.barra}`} />

              <span className={`min-w-0 flex-1 px-2 ${compacta ? 'py-0' : 'py-1'}`}>
                <span
                  className={`flex items-center gap-1 ${compacta ? 'leading-[22px]' : 'leading-tight'}`}
                >
                  {cita.tentativa_local && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50"
                      title="sin confirmar"
                    />
                  )}
                  <span className="truncate text-[13px] font-semibold">
                    {cliente?.nombre ?? 'Sin cliente'}
                  </span>
                </span>

                {!compacta && (
                  <span className="mt-0.5 block truncate text-[11px] tabular-nums opacity-75">
                    {hora(tramo.inicio)}–{hora(tramo.fin)}
                    {cerrada && ` · ${etiquetaEstado(cita.estado)}`}
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function etiquetaEstado(estado: string): string {
  const etiquetas: Record<string, string> = {
    pendiente: 'pendiente',
    confirmada: 'confirmada',
    en_atencion: 'en atención',
    atendida: 'atendida',
    facturada: 'facturada',
  }
  return etiquetas[estado] ?? estado
}
